import { useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { API_BASE, NET_QUALITY_CONFIG } from '../constants';
import { loadSocketIO } from '../utils';

/**
 * Manages the Socket.IO connection for live location pings and task submissions.
 *
 * Params:
 *   token               – auth token
 *   activeTrailRowRef   – ref pointing to the currently-viewed row
 *   trailRef            – ref pointing to the current trail array
 *   setTrail            – state setter for trail
 *   setTasks            – state setter for tasks
 *   mapInstance         – ref to the Leaflet map instance
 *   leafletRef          – ref to the Leaflet library
 *   trailPolylineRef    – ref to the trail polyline layer
 *   trailLayersRef      – ref to trail layer array (for cleanup)
 *   lastTrailDotRef     – ref to the most-recently-drawn trail dot
 *   activityMarkersRef  – ref to array of per-session map markers
 *   openEntryModalRef   – ref to function (entry) => void
 *
 * Returns:
 *   socketRef       – ref to the socket instance
 *   socketConnected – boolean state
 */
export function useSocket({
  token,
  activeTrailRowRef,
  trailRef,
  setTrail,
  setTasks,
  mapInstance,
  leafletRef,
  trailPolylineRef,
  trailLayersRef,
  lastTrailDotRef,
  activityMarkersRef,
  openEntryModalRef,
}) {
  const socketRef       = useRef(null);
  const [socketConnected, setSocketConnected] = useState(false);

  useEffect(() => {
    if (!token) return;
    let socket;

    loadSocketIO().then((io) => {
      socket = io(`${API_BASE}/location`, {
        path: '/socket.io',
        auth: { token },
        transports: ['websocket', 'polling'],
        reconnectionDelay: 2000,
        reconnectionAttempts: 10,
      });

      socketRef.current = socket;

      socket.on('connect', () => {
        setSocketConnected(true);
        socket.emit('subscribe:activity');
        const activeRow = activeTrailRowRef.current;
        if (activeRow) {
          const sessionId = activeRow.checkin?.code ?? activeRow.checkin?._id;
          if (sessionId) socket.emit('subscribe:session', sessionId);
        }
      });

      socket.on('disconnect', () => setSocketConnected(false));

      // ── Live location ping ─────────────────────────────────────────────
      socket.on('location:ping', (ping) => {
        const activeRow = activeTrailRowRef.current;
        if (!activeRow) return;

        const sessionId = activeRow.checkin?.code ?? activeRow.checkin?._id;
        if (ping.sessionId !== String(sessionId)) return;
        if (!ping.location?.latitude) return;

        const alreadyHave = trailRef.current.some((p) => String(p._id) === String(ping._id));
        if (alreadyHave) return;

        const newTrail = [...trailRef.current, ping];
        trailRef.current = newTrail;
        setTrail(newTrail);

        if (!mapInstance.current || !leafletRef.current) return;

        const L        = leafletRef.current;
        const idx      = newTrail.length - 1;
        const dotSize  = 10;
        const ORANGE   = '#c06000';
        const BLUE     = '#91ce0f';

        // Demote the previous "latest" dot from orange back to blue
        if (lastTrailDotRef.current) {
          lastTrailDotRef.current.setIcon(L.divIcon({
            html: `<div style="width:${dotSize}px;height:${dotSize}px;border-radius:50%;background:${BLUE};border:2.5px solid #fff;box-shadow:0 2px 8px rgba(15,33,55,0.25);cursor:pointer;"></div>`,
            className: '', iconSize: [dotSize, dotSize],
            iconAnchor: [dotSize / 2, dotSize / 2], popupAnchor: [0, -(dotSize / 2 + 6)],
          }));
        }

        const netCfg  = NET_QUALITY_CONFIG[ping.deviceContext?.networkQuality ?? 'unknown'];
        const ts      = ping.timestamp ? format(new Date(ping.timestamp), "MMM d 'at' hh:mm a") : '-';
        const lateTag = ping.isLate ? `<span style="font-size:10px;color:#ef4444;background:#fef2f2;border:1px solid #fecaca;padding:1px 5px;border-radius:4px;font-weight:700;">Late +${ping.latenessMinutes}m</span>` : '';
        const offlineTag = ping.wasOfflineQueued ? `<span style="font-size:10px;color:#f59e0b;background:#fffbeb;border:1px solid #fde68a;padding:1px 5px;border-radius:4px;font-weight:700;">Offline queued</span>` : '';

        const dotIcon = L.divIcon({
          html: `<div style="width:${dotSize}px;height:${dotSize}px;border-radius:50%;background:${ORANGE};border:2.5px solid #fff;box-shadow:0 2px 8px rgba(15,33,55,0.25),0 0 0 2px ${ORANGE}40;cursor:pointer;animation:pulseNew 1.2s ease-out 1;"></div>`,
          className: '', iconSize: [dotSize, dotSize],
          iconAnchor: [dotSize / 2, dotSize / 2], popupAnchor: [0, -(dotSize / 2 + 6)],
        });

        const popupHtml = `
          <div style="padding:12px 14px;min-width:200px;font-family:'DM Sans',sans-serif;">
            <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:8px;">
              <div style="display:flex;align-items:center;gap:6px;">
                <div style="width:8px;height:8px;border-radius:50%;background:#22c55e;animation:blink 1s infinite;"></div>
                <span style="font-size:12px;font-weight:700;color:#0f2137;">Live ping #${idx + 1}</span>
              </div>
              <div style="display:flex;gap:4px;">${lateTag}${offlineTag}</div>
            </div>
            <div style="padding-top:8px;border-top:1px solid #eef2f7;display:grid;gap:5px;">
              <div style="display:flex;justify-content:space-between;">
                <span style="font-size:11px;color:#6b7f8e;">Time</span>
                <span style="font-size:11px;font-weight:600;color:#0f2137;font-family:monospace;">${ts}</span>
              </div>
              <div style="display:flex;justify-content:space-between;">
                <span style="font-size:11px;color:#6b7f8e;">Coords</span>
                <span style="font-size:10px;color:#4a6a85;font-family:monospace;">${ping.location.latitude.toFixed(5)}, ${ping.location.longitude.toFixed(5)}</span>
              </div>
              <div style="display:flex;justify-content:space-between;">
                <span style="font-size:11px;color:#6b7f8e;">Network</span>
                <span style="font-size:11px;font-weight:600;color:${netCfg.color};">${netCfg.label}</span>
              </div>
            </div>
          </div>`;

        const dot = L.marker([ping.location.latitude, ping.location.longitude], { icon: dotIcon })
          .addTo(mapInstance.current);
        dot.bindPopup(popupHtml, { maxWidth: 260, closeButton: false, className: 'trail-popup' });
        dot.on('mouseover', () => dot.openPopup());
        dot.on('mouseout', () => dot.closePopup());
        trailLayersRef.current.push(dot);
        lastTrailDotRef.current = dot;

        if (trailPolylineRef.current) {
          trailPolylineRef.current.addLatLng([ping.location.latitude, ping.location.longitude]);
        }
      });

      // ── Live task submission ───────────────────────────────────────────
      socket.on('task:submitted', (entry) => {
        const activeRow = activeTrailRowRef.current;
        if (!activeRow) return;

        const activeUserId  = activeRow.author?._id ?? activeRow.author?.id;
        const entryAuthorId = typeof entry.author === 'object'
          ? (entry.author?._id ?? entry.author?.id)
          : entry.author;

        if (String(activeUserId) !== String(entryAuthorId)) return;

        const liveEntry = { ...entry, _isLive: true };

        setTasks((prev) => {
          if (prev.some((t) => String(t._id) === String(entry._id))) return prev;
          return [...prev, liveEntry];
        });

        // Drop a diamond marker on the map for the task location
        if (!mapInstance.current || !leafletRef.current) return;
        const loc = entry.formData?.location;
        if (!loc) return;
        const lat = parseFloat(loc.latitude ?? loc.lat);
        const lng = parseFloat(loc.longitude ?? loc.lng ?? loc.long);
        if (isNaN(lat) || isNaN(lng)) return;

        const L         = leafletRef.current;
        const taskTime  = entry.createdAt ? format(new Date(entry.createdAt), 'hh:mm a') : '—';
        const taskLabel = entry.taskLabel || entry.taskId || 'Task';
        const syncTag   = entry.synced
          ? '<span style="font-size:9px;color:#0a9e74;background:#e6f9f4;border:1px solid #b3ead9;padding:1px 5px;border-radius:3px;font-weight:700;">Synced</span>'
          : '<span style="font-size:9px;color:#c06000;background:#fff4e0;border:1px solid #ffd599;padding:1px 5px;border-radius:3px;font-weight:700;">Offline</span>';

        const diamondHtml = `
          <div style="width:28px;height:28px;display:flex;align-items:center;justify-content:center;">
            <div style="width:18px;height:18px;background:linear-gradient(135deg,#0a9e74,#5f940a);border:2.5px solid #fff;border-radius:4px;transform:rotate(45deg);box-shadow:0 2px 8px rgba(10,158,116,0.5),0 0 0 3px rgba(10,158,116,0.2);animation:pulseNew 1.2s ease-out 1;"></div>
          </div>`;

        const icon = L.divIcon({ html: diamondHtml, className: '', iconSize: [28, 28], iconAnchor: [14, 14], popupAnchor: [0, -18] });

        const popupHtml = `
          <div style="padding:11px 13px;min-width:200px;font-family:DM Sans,sans-serif;">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">
              <div style="width:8px;height:8px;border-radius:50%;background:#22c55e;animation:blink 1s infinite;flex-shrink:0;"></div>
              <span style="font-size:12px;font-weight:700;color:#0f2137;">Live task</span>
              ${syncTag}
            </div>
            <div style="font-weight:600;font-size:12px;color:#0f2137;margin-bottom:4px;">${taskLabel}</div>
            <div style="font-size:11px;color:#6b7f8e;font-family:monospace;">${taskTime}</div>
            <div style="margin-top:6px;font-size:11px;color:#91ce0f;font-weight:600;cursor:pointer;">Click to view details →</div>
          </div>`;

        const taskMarker = L.marker([lat, lng], { icon }).addTo(mapInstance.current);
        taskMarker.bindPopup(popupHtml, { maxWidth: 240, closeButton: false, className: 'trail-popup' });
        taskMarker.on('mouseover', () => taskMarker.openPopup());
        taskMarker.on('mouseout', () => taskMarker.closePopup());
        taskMarker.on('click', () => { taskMarker.closePopup(); openEntryModalRef.current?.(liveEntry); });
        activityMarkersRef.current.push(taskMarker);

        if (trailPolylineRef.current) {
          trailPolylineRef.current.addLatLng([lat, lng]);
        }
      });
    });

    return () => {
      if (socket) {
        socket.emit('unsubscribe:activity');
        socket.disconnect();
        socketRef.current = null;
      }
    };
  }, [token]); 
  // NOTE: refs are intentionally excluded from deps — they are stable by design.

  return { socketRef, socketConnected };
}
